import { createAudioEngine, type AudioEngine, type LoopHandle } from './engine';
import type { CueId } from './cues';

// The client's one audio service: the engine plus the persisted mute, with the music/ambient
// beds as named states rather than raw loop handles. Created once in main.ts and passed down —
// no module-level singleton, so a test can build its own.
//
// Mute is deliberately MASTER and persisted: the design doc's whole "silence is a resource"
// argument only works if a player who dislikes the bed can silence it without also losing the
// cues that carry information — so the bed is quiet by default and mute is one tap, everywhere.

export interface MuteStore {
  isMuted(): boolean;
  setMuted(muted: boolean): void;
}

export interface AudioServiceOpts {
  readonly store: MuteStore;
  /** Injected for tests; production builds a real engine. */
  readonly engine?: AudioEngine;
}

export type MusicBed = 'music-menu' | 'music-game';

export interface AudioService {
  /** MUST be reached synchronously from a gesture handler (engine §2.6). */
  unlock(): void;
  isMuted(): boolean;
  setMuted(muted: boolean): void;
  /** Fires whenever mute changes from anywhere — the two toggles use it to stay in sync. */
  onMuteChange(handler: () => void): () => void;
  cue(id: CueId): void;
  batch(cues: readonly CueId[]): void;
  /** Idempotent: asking for the bed already playing does nothing. */
  music(bed: MusicBed | null): void;
  ambient(on: boolean): void;
  dispose(): void;
}

export function createAudioService(opts: AudioServiceOpts): AudioService {
  const engine = opts.engine ?? createAudioEngine({ muted: opts.store.isMuted() });
  const handlers = new Set<() => void>();
  let bed: MusicBed | null = null;
  // Every bed still making sound. Retirement is NOT a chain from each bed to its immediate
  // predecessor: a bed superseded while still loading never settles, so it would never
  // retire the one before it and two beds would play at once. Whichever bed starts retires
  // ALL the others.
  const liveBeds = new Set<LoopHandle>();
  let surf: LoopHandle | null = null;

  return {
    unlock: () => engine.unlock(),
    isMuted: () => opts.store.isMuted(),
    setMuted(muted: boolean): void {
      opts.store.setMuted(muted);
      engine.setMuted(muted);
      for (const h of handlers) h();
    },
    onMuteChange(handler: () => void): () => void {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    cue: (id) => engine.play(id),
    batch: (cues) => engine.playBatch(cues),
    /**
     * Swap the bed by CROSSFADE, never a cut.
     *
     * The outgoing bed is held at full until the incoming one has really started — its
     * buffer may still be in flight, and a fade-out begun on request would leave a silent
     * gap on a cold fetch. `onSettled` also fires when the incoming bed FAILS to load, so a
     * bed that cannot start still retires the old one rather than stranding the menu music
     * over a game.
     *
     * A swap that arrives while the previous incoming bed is still loading is safe: stopping
     * a not-yet-started loop marks it stopped, so it never starts and leaves no orphan.
     */
    music(next: MusicBed | null): void {
      if (next === bed) return;
      bed = next;
      if (next === null) {
        for (const handle of liveBeds) handle.stop();
        liveBeds.clear();
        return;
      }
      // Snapshot what is live BEFORE the incoming bed exists, so it can never retire itself.
      // Identity-comparing against `bedHandle` instead would break on a cached buffer, where
      // `onSettled` runs synchronously inside `loop()` while `bedHandle` still points at the
      // OUTGOING bed — skipping exactly the one that needed to go.
      const outgoing = [...liveBeds];
      const retireOutgoing = (): void => {
        for (const handle of outgoing) {
          handle.stop();
          liveBeds.delete(handle);
        }
      };
      liveBeds.add(engine.loop(next, { onSettled: retireOutgoing }));
    },
    ambient(on: boolean): void {
      if (on === (surf !== null)) return;
      if (on) surf = engine.loop('surf');
      else {
        surf?.stop();
        surf = null;
      }
    },
    dispose(): void {
      for (const handle of liveBeds) handle.stop(0);
      liveBeds.clear();
      surf?.stop(0);
      surf = null;
      bed = null;
      handlers.clear();
      engine.dispose();
    },
  };
}
